import React, {useState} from 'react';
import OutsideClickHandler from 'react-outside-click-handler/esm/OutsideClickHandler';
import {useTranslation} from 'react-i18next';

import ControlButton from 'components/Interview/OnlineInterview/RightPanel/Participants/Controls/ControlButton';
import CameraIcon from 'components/common/icons/onlineInterview/CameraIcon';
import DisabledCameraIcon from 'components/common/icons/onlineInterview/DisabledCameraIcon';
import MicrophoneIcon from 'components/common/icons/onlineInterview/MicrophoneIcon';
import DisabledMicrophoneIcon from 'components/common/icons/onlineInterview/DisabledMicrophoneIcon';
import SoundIcon from 'components/common/icons/onlineInterview/SoundIcon';
import DisabledSoundIcon from 'components/common/icons/onlineInterview/DisabledSoundIcon';
import ScreenSharingIcon from 'components/common/icons/onlineInterview/ScreenSharingIcon';
import DisabledScreenSharingIcon from 'components/common/icons/onlineInterview/DisabledScreenSharingIcon';
import TickIcon from 'components/common/icons/TickIcon';
import EditIcon from 'components/common/icons/redesign/EditIcon';
import OnlineInterviewMenu from 'components/common/icons/redesign/OnlineInterviewMenu';

import styles from './Controls.module.scss';

function ModeratorControls({
    capabilities,
    changeCapabilities,
    isSoundEnabled,
    isStreamingAudio,
    changeSoundEnabled,
    isEditingName,
    onEditNameToggle,
    onKick,
    moveToWaitingRoom,
    isTimeout,
    isChangeAudioCapabilitiesAvailable,
    isModerator,
}) {
    const {t} = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuHovered, setMenuHovered] = useState(false);

    const changeCapability = (capabilityType, capabilityValue) =>
        changeCapabilities({
            ...capabilities,
            [capabilityType]: capabilityValue,
        });

    if (isTimeout) {
        return <div className={styles.timeout}>{t('waitingRoom:timeout')}</div>;
    }

    return (
        <div className={styles.controls}>
            <ControlButton
                value={isEditingName}
                enabledIcon={TickIcon}
                disabledIcon={EditIcon}
                changeValue={onEditNameToggle}
                enableText={t('onlineInterview:tooltips.changeName')}
                disableText={t('onlineInterview:tooltips.confirmNameChange')}
            />
            {isStreamingAudio && (
                <ControlButton
                    changeValue={changeSoundEnabled}
                    value={isSoundEnabled}
                    enabledIcon={SoundIcon}
                    disabledIcon={DisabledSoundIcon}
                    enableText={t('onlineInterview:tooltips.enableAudio')}
                    disableText={t('onlineInterview:tooltips.disableAudio')}
                />
            )}
            <ControlButton
                changeValue={changeCapability.bind(null, 'screenSharing')}
                value={capabilities.screenSharing}
                enabledIcon={ScreenSharingIcon}
                disabledIcon={DisabledScreenSharingIcon}
                enableText={t('onlineInterview:tooltips.allowScreenShare')}
                disableText={t('onlineInterview:tooltips.disableScreenSharing')}
            />
            <ControlButton
                changeValue={changeCapability.bind(null, 'video')}
                value={capabilities.video}
                enabledIcon={CameraIcon}
                disabledIcon={DisabledCameraIcon}
                enableText={t('onlineInterview:tooltips.enableVideo')}
                disableText={t('onlineInterview:tooltips.disableVideo')}
            />
            {isChangeAudioCapabilitiesAvailable && (
                <ControlButton
                    changeValue={changeCapability.bind(null, 'audio')}
                    value={capabilities.audio}
                    enabledIcon={MicrophoneIcon}
                    disabledIcon={DisabledMicrophoneIcon}
                    disableText={t('onlineInterview:tooltips.mute')}
                    enableText={t('onlineInterview:tooltips.unmute')}
                />
            )}
            <div className={styles.menuContainer}>
                <OnlineInterviewMenu
                    className={styles.icon}
                    onClick={() => setMenuOpen(!menuOpen)}
                    onMouseEnter={() => setMenuHovered(true)}
                    onMouseLeave={() => setMenuHovered(false)}
                />
                {menuOpen && (
                    <OutsideClickHandler
                        onOutsideClick={() => {
                            if (menuHovered) return;
                            setMenuOpen(false);
                        }}
                    >
                        <div className={styles.menu}>
                            {!isModerator && (
                                <div onClick={moveToWaitingRoom}>{t('onlineInterview:moveToWaiting')}</div>
                            )}
                            <div className={styles.danger} onClick={onKick}>
                                {t('common.remove')}
                            </div>
                        </div>
                    </OutsideClickHandler>
                )}
            </div>
        </div>
    );
}

export default React.memo(ModeratorControls);
